import { defModule, Update, type UpdateResult } from '@st/redux'
import type { GetOrCreateFolderResponse, SDKRequestFor } from '@st/sdk'
import {
  buildSchemaId,
  CURRENT_TAX_YEAR,
  RETURN_TYPES,
  supportedReturnTypes,
  type ReturnType,
  type TaxYear
} from '@st/tax-folder'
import { isValidEmail } from '@st/util/email'
import { isNotEmpty } from '@st/util/json-value'
import { Effect, pipe } from 'effect'
import { isMatching, match, P } from '@st/pattern'
import { SdkClient } from '~/effects'
import { runSdkRequest } from '~/features/module-runtime'

type FolderCreateOneByOneForm = {
  year: TaxYear
  returnType: ReturnType
  firstName: string
  lastName: string
  corporationName: string
  email: string
}

type FolderCreateContext = {
  form: FolderCreateOneByOneForm
  confirmDialogVisible: boolean
  lastResponse: GetOrCreateFolderResponse | undefined
}

type FolderCreationRequest = {
  organizationId: string
  schemaId: string
  email: string
  firstName: string
  lastName: string
  corporationName: string
  confirmOnRelatedFolders: boolean
}

type FolderCreateReadyState = {
  status: 'ready'
  organizationId: string
} & FolderCreateContext

type FolderCreateRunningState = {
  status: 'running'
  organizationId: string
  request: FolderCreationRequest
} & FolderCreateContext

type FolderCreateFailedState = {
  status: 'failed'
  organizationId: string
  request: FolderCreationRequest
} & FolderCreateContext

type FolderCreatePendingState = FolderCreateRunningState | FolderCreateFailedState

export type FolderCreateOneByOneState = FolderCreateReadyState | FolderCreatePendingState

type SetFieldMessage =
  | { type: 'setField'; field: 'year'; value: TaxYear }
  | { type: 'setField'; field: 'returnType'; value: ReturnType }
  | { type: 'setField'; field: 'firstName'; value: string }
  | { type: 'setField'; field: 'lastName'; value: string }
  | { type: 'setField'; field: 'corporationName'; value: string }
  | { type: 'setField'; field: 'email'; value: string }

export type FolderCreateOneByOneMessage =
  | SetFieldMessage
  | { type: 'addClient'; confirmOnRelatedFolders: boolean }
  | { type: 'retryFolderCreation' }
  | { type: 'folderCreationCompleted'; response: GetOrCreateFolderResponse }
  | { type: 'folderCreationFailed' }
  | { type: 'closeConfirmDialog' }

/**
 * Folder creation is an accepted durable write:
 *
 * ready -> running -> ready
 *              \-> failed -> running
 *
 * It is a Command, so the parent may hide this child while `running` without
 * canceling the server mutation. `failed` keeps the exact accepted request for
 * an explicit retry. The phase owns the one outcome; no request identity is
 * copied through the message protocol.
 */
export const folderCreateOneByOneModule = defModule<{
  state: FolderCreateOneByOneState
  message: FolderCreateOneByOneMessage
  init: string
}>({
  init: folderCreationSession,
  update: pipe(
    updateFolderCreateOneByOne,
    Update.ignoreWhen<FolderCreateOneByOneState, FolderCreateOneByOneMessage>(
      isMatching({
        state: { status: 'running' },
        message: { type: P.union('setField', 'addClient', 'retryFolderCreation') }
      })
    )
  )
})

function updateFolderCreateOneByOne(
  state: FolderCreateOneByOneState,
  message: FolderCreateOneByOneMessage
): UpdateResult<FolderCreateOneByOneState, FolderCreateOneByOneMessage> {
  return match({ state, message })
    .returnType<UpdateResult<FolderCreateOneByOneState, FolderCreateOneByOneMessage>>()
    .with(
      { state: { status: P.union('ready', 'failed') }, message: { type: 'setField' } },
      ({ state, message }) => ({
        ...folderCreationReady(state),
        form: updateForm(state.form, message)
      })
    )
    .with({ state: { status: 'ready' }, message: { type: 'addClient' } }, ({ state, message }) =>
      startFolderCreation(state, message.confirmOnRelatedFolders)
    )
    .with({ state: { status: 'failed' }, message: { type: 'retryFolderCreation' } }, ({ state }) =>
      retryFolderCreation(state)
    )
    .with({ message: { type: 'closeConfirmDialog' } }, ({ state }) => ({
      ...state,
      confirmDialogVisible: false
    }))
    .with(
      { state: { status: 'running' }, message: { type: 'folderCreationCompleted' } },
      ({ state, message }) => folderCreationCompleted(state, message.response)
    )
    .with(
      { state: { status: 'running' }, message: { type: 'folderCreationFailed' } },
      ({ state }) => ({ ...state, status: 'failed' })
    )
    .otherwise(({ state, message }) => unexpectedMessage(state, message))
}

export function selRelatedFolders(state: FolderCreateOneByOneState) {
  return state.lastResponse?.relatedFolders ?? []
}

export function selCanAddClient(state: FolderCreateOneByOneState) {
  return (
    state.status == 'ready' &&
    isValidEmail(state.form.email) &&
    isNotEmpty(state.form.firstName) &&
    isNotEmpty(state.form.lastName)
  )
}

function folderCreationSession(organizationId: string): FolderCreateOneByOneState {
  return {
    status: 'ready',
    organizationId,
    form: {
      year: CURRENT_TAX_YEAR,
      returnType: RETURN_TYPES[0],
      firstName: '',
      lastName: '',
      corporationName: '',
      email: ''
    },
    confirmDialogVisible: false,
    lastResponse: undefined
  }
}

function updateForm(
  form: FolderCreateOneByOneForm,
  message: SetFieldMessage
): FolderCreateOneByOneForm {
  const updated = match(message)
    .returnType<FolderCreateOneByOneForm>()
    .with({ field: 'year' }, ({ value }) => ({ ...form, year: value }))
    .with({ field: 'returnType' }, ({ value }) => ({ ...form, returnType: value }))
    .with({ field: 'firstName' }, ({ value }) => ({ ...form, firstName: value }))
    .with({ field: 'lastName' }, ({ value }) => ({ ...form, lastName: value }))
    .with({ field: 'corporationName' }, ({ value }) => ({ ...form, corporationName: value }))
    .with({ field: 'email' }, ({ value }) => ({ ...form, email: value }))
    .exhaustive()

  const returnTypes = supportedReturnTypes(updated.year)
  if (returnTypes.includes(updated.returnType)) return updated
  return { ...updated, returnType: returnTypes[0] as ReturnType }
}

function folderCreationCompleted(
  state: FolderCreateRunningState,
  response: GetOrCreateFolderResponse
): FolderCreateOneByOneState {
  const ready = folderCreationReady(state)
  return match(response)
    .returnType<FolderCreateOneByOneState>()
    .with({ status: 'created' }, () => ({
      ...ready,
      confirmDialogVisible: false,
      form: {
        ...ready.form,
        email: '',
        firstName: '',
        lastName: '',
        corporationName: ''
      },
      lastResponse: response
    }))
    .with({ status: 'needs_confirmation' }, () => ({
      ...ready,
      confirmDialogVisible: true,
      lastResponse: response
    }))
    .exhaustive()
}

function startFolderCreation(
  state: FolderCreateReadyState,
  confirmOnRelatedFolders: boolean
): UpdateResult<FolderCreateOneByOneState, FolderCreateOneByOneMessage> {
  const request = {
    organizationId: state.organizationId,
    schemaId: buildSchemaId(state.form.year, state.form.returnType),
    email: state.form.email,
    firstName: state.form.firstName,
    lastName: state.form.lastName,
    corporationName: state.form.corporationName,
    confirmOnRelatedFolders
  }

  return [
    {
      ...state,
      status: 'running',
      lastResponse: undefined,
      request
    },
    createFolder(request)
  ]
}

function retryFolderCreation(
  state: FolderCreateFailedState
): UpdateResult<FolderCreateOneByOneState, FolderCreateOneByOneMessage> {
  return [{ ...state, status: 'running' }, createFolder(state.request)]
}

function createFolder(request: FolderCreationRequest) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* runSdkRequest(
      sdk.send({
        type: 'folders/getOrCreateFolder',
        ...request
      } satisfies SDKRequestFor<'folders/getOrCreateFolder'>)
    )

    if (result.ok) {
      return { type: 'folderCreationCompleted', response: result.response } as const
    }
    return { type: 'folderCreationFailed' } as const
  })
}

function folderCreationReady(state: FolderCreateOneByOneState): FolderCreateReadyState {
  return {
    status: 'ready',
    organizationId: state.organizationId,
    form: state.form,
    confirmDialogVisible: state.confirmDialogVisible,
    lastResponse: state.lastResponse
  }
}

function unexpectedMessage(
  state: FolderCreateOneByOneState,
  message: FolderCreateOneByOneMessage
): never {
  throw new Error(`Unexpected ${message.type} message in ${state.status} folder creation state`)
}
